import { useCallback, useEffect, useRef, useState } from 'react';
import { useRouter } from 'next/router';
import { useStytchUser } from '@stytch/nextjs';

import LoginLayout from 'components/container-components/layout/LoginLayout';
import { AuthRoutes, CommonRoutes } from 'routes';
import Button from 'components/app-components/ui/button';
import LoadingSpinner from 'icons/LoadingSpinner';
import CheckMark from 'icons/CheckMark';
import { useUser } from 'context/user';
import LoadingScreen from 'components/container-components/LoadingScreen';
import { IUser } from 'types';
import useStytchVerifyEmail from 'hooks/useVerifyEmail';
import CloseIcon from 'icons/Close';
import { emailRegex } from 'utils/regex';
import { hasUsedEmailUpdateModal_LS_key } from 'components/app-components/account-preferences/general-tab/ChangeEmailModal';
import { checkIfEmailVerified } from 'utils/user';

type _IUser = IUser & { isEmailVerified?: boolean };

export const hasSentFirstVerifyEmail_LS_key = 'hasSentFirstVerifyEmail_LS_key';

export default function VerifyEmailPage() {
  const { user, isLoading: isUserLoading, logout, isLoggingOut, roleType, mutate: refreshUserProfile } = useUser();
  const { user: stytchUser } = useStytchUser();
  const router = useRouter();
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [timeLastEmailSent, setTimeLastEmailSent] = useState<number>(null);
  const [countdown, setCountdown] = useState(30);
  const [firstEmailSent, setFirstEmailSent] = useState<boolean>();
  const [checkingLocalStorage, setCheckingLocalStorage] = useState(true);
  // The localStorage flag can't stop a repeat send on its own: verifying in another tab clears it
  // while this page is still open.
  const hasHandledFirstEmailRef = useRef(false);

  const { email: email_ } = router.query;
  const email = decodeURIComponent(email_ as string);
  const isValidEmail = emailRegex.test(email);

  const onEmailSent = useCallback(() => {
    setMagicLinkSent(true);
    setTimeLastEmailSent(new Date().getTime());
  }, []);
  const { sendVerificationEmail, isSending } = useStytchVerifyEmail({ onSuccess: onEmailSent });

  const sendEmail = useCallback(async () => {
    await sendVerificationEmail(email);
    setFirstEmailSent(true);
  }, [email, sendVerificationEmail]);

  useEffect(() => {
    // isEmailVerified stays undefined until the profile loads, and the email arrives with the query.
    // The send below happens once per page load, so it waits until both are known.
    if (!router.isReady || checkingLocalStorage || user?.isEmailVerified === undefined) return;

    // If user is already logged in and verified, redirect to dashboard
    if (user.isEmailVerified) {
      localStorage.removeItem(hasSentFirstVerifyEmail_LS_key);
      localStorage.removeItem(hasUsedEmailUpdateModal_LS_key);
      if (!user.isOnboarded && roleType === 'worker') {
        router.replace(AuthRoutes.SignUpWorkerOnboarding);
        return;
      }
      router.replace(CommonRoutes.Dashboard);
      return;
    }

    if (!isValidEmail || hasHandledFirstEmailRef.current) return;
    hasHandledFirstEmailRef.current = true;

    const hasSentFirstVerificationEmail = localStorage.getItem(hasSentFirstVerifyEmail_LS_key);
    const hasUsedEmailUpdateModal = localStorage.getItem(hasUsedEmailUpdateModal_LS_key);
    if (hasSentFirstVerificationEmail || hasUsedEmailUpdateModal) {
      setFirstEmailSent(true);
      setMagicLinkSent(true);
      return;
    }

    localStorage.setItem(hasSentFirstVerifyEmail_LS_key, email);
    sendEmail();
  }, [checkingLocalStorage, email, isValidEmail, roleType, router, sendEmail, user]);

  // Verifying in another tab syncs the Stytch user into this one, but the profile only reloads when
  // the signed-in user changes. Reload it here so this tab moves on the way a page refresh would.
  // A different user signing in is left to UserProvider, which already reloads on that.
  useEffect(() => {
    if (user?.isEmailVerified !== false || stytchUser?.user_id !== user.userID) return;
    let isCurrent = true;
    checkIfEmailVerified(stytchUser).then((isVerifiedOnStytch) => {
      if (isCurrent && isVerifiedOnStytch) refreshUserProfile();
    });
    return () => {
      isCurrent = false;
    };
  }, [refreshUserProfile, stytchUser, user?.isEmailVerified, user?.userID]);

  // upon mount, set checkingLocalStorage to false
  useEffect(() => {
    setCheckingLocalStorage(false);
  }, []);

  // Countdown timer
  useEffect(() => {
    let interval: NodeJS.Timeout | undefined;
    if (timeLastEmailSent !== null) {
      interval = setInterval(() => {
        const secondsPassed = Math.floor((new Date().getTime() - timeLastEmailSent) / 1000);
        const remainingSeconds = 30 - secondsPassed;
        setCountdown(remainingSeconds);
        if (remainingSeconds <= 0) {
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [timeLastEmailSent]);

  const canResendEmail = timeLastEmailSent === null || countdown <= 0;

  const resendEmail = () => {
    if (canResendEmail) {
      setMagicLinkSent(false);
      sendEmail();
    }
  };

  if (!email) {
    return router.push(AuthRoutes.SignIn);
  }

  if (email_ && !isValidEmail) {
    return <div className="my-20 text-center">Invalid Email!</div>;
  }

  if (isUserLoading || checkingLocalStorage) {
    return <LoadingScreen />;
  }

  return (
    <div className="relative">
      <div className="fixed right-4 top-4 z-50 md:right-12 md:top-12">
        <Button
          variant="default"
          className="flex h-12 w-12 items-center justify-center"
          aria-label="Log out"
          onClick={() => logout()}
          loading={isLoggingOut}
          disabled={isLoggingOut}
        >
          <CloseIcon className="h-7 w-7" />
        </Button>
      </div>
      <LoginLayout pageTitle="Please verify your email" showRightContainer={false} isVerifyEmailPage={true}>
        <div className="mt-6 flex flex-col items-center justify-center">
          {isSending ? (
            <LoadingSpinner width={20} height={20} className="mb-4 opacity-0" />
          ) : magicLinkSent ? (
            <CheckMark className="mb-4 h-7 w-7" />
          ) : null}
          <p className="text-center text-base font-normal text-neutral-800">
            Welcome! We&apos;{firstEmailSent ? 've sent' : 'll send'} you an email to{' '}
            <span className="font-semibold">{email}</span> with a verification link to get started. Click the link and
            you&apos;re all set.
          </p>
          <div className="mt-4">
            <p className="flex items-center text-sm">
              {firstEmailSent ? 'Didn’t receive a link?' : 'Ready to verify?'}{' '}
              <Button
                className="ml-2"
                onClick={firstEmailSent ? resendEmail : sendEmail}
                variant={firstEmailSent ? 'plain' : 'primary'}
                disabled={!canResendEmail || isSending}
                loading={isSending}
              >
                <span className={firstEmailSent && canResendEmail ? 'underline' : ''}>
                  {firstEmailSent ? (canResendEmail ? 'Resend Email' : `Resend in ${countdown}s`) : 'Send Email'}
                </span>
              </Button>
            </p>
          </div>
        </div>
      </LoginLayout>
    </div>
  );
}
